import React, { useEffect, useState } from "react";
import Nav from "react-bootstrap/Nav";
import Navbar from "react-bootstrap/Navbar";
import { NavLink, useLocation, useNavigate } from "react-router-dom";
import { FaChevronRight, FaHeadset } from "react-icons/fa";
import Offcanvas from "react-bootstrap/Offcanvas";
import Logo from "../../assets/image/Logo.svg";
import DashboardIcon from "../../assets/image/Dashboard.svg";
import DocumentManagementIcon from "../../assets/image/Document.svg";
import ServiceManagementIcon from "../../assets/image/Service.svg";
import BookingManagementIcon from "../../assets/image/Booking.svg";
import PackageManagementIcon from "../../assets/image/Package.svg";
import TermsAndConditionIcon from "../../assets/image/Terms.svg";
import { useDataContext } from "../DataContext";
import vendorDefaultAvatar from "../../utils/vendorDefaultAvatar";
import { getBookingManagementNavLinkState } from "../../Pages/BookingManagement/bookingListSessionUi";
import {
  resolveVendorServiceType,
  vendorModuleAccess,
} from "../../utils/vendorServiceAccess";

const Sidebar = () => {
  const { modules, vendorProfile } = useDataContext();
  const routerLocation = useLocation();
  const navigate = useNavigate();
  const [loadedModules, setLoadedModules] = useState([]);
  const serviceType = resolveVendorServiceType(vendorProfile);
  const { shop: canShop, rent: canRent, service: canService } =
    vendorModuleAccess(serviceType);
  const productHome = canRent && !canShop
    ? "/product-management/rent"
    : "/product-management/shop";
  const merchantHome = canRent && !canShop
    ? "/merchant-management/rent"
    : "/merchant-management/shop";
  const inProductMgmt = routerLocation.pathname.startsWith("/product-management");
  const [productMenuOpen, setProductMenuOpen] = useState(inProductMgmt);
  const inMerchantMgmt = routerLocation.pathname.startsWith("/merchant-management");
  const [merchantMenuOpen, setMerchantMenuOpen] = useState(inMerchantMgmt);
  const inServiceMgmt = routerLocation.pathname.startsWith("/service-management");
  const [serviceMenuOpen, setServiceMenuOpen] = useState(inServiceMgmt);
  const inBookingMgmt = routerLocation.pathname.startsWith("/booking-management");
  const [bookingMenuOpen, setBookingMenuOpen] = useState(inBookingMgmt);
  const inPackageMgmt = routerLocation.pathname.startsWith("/package-management");
  const [packageMenuOpen, setPackageMenuOpen] = useState(inPackageMgmt);

  /* Open submenu when entering Shop/Rent; close when navigating to any other module */
  useEffect(() => {
    if (inProductMgmt) {
      setProductMenuOpen(true);
    } else {
      setProductMenuOpen(false);
    }
  }, [inProductMgmt]);

  useEffect(() => {
    if (inMerchantMgmt) {
      setMerchantMenuOpen(true);
    } else {
      setMerchantMenuOpen(false);
    }
  }, [inMerchantMgmt]);

  useEffect(() => {
    if (inServiceMgmt) {
      setServiceMenuOpen(true);
    } else {
      setServiceMenuOpen(false);
    }
  }, [inServiceMgmt]);

  useEffect(() => {
    if (inBookingMgmt) {
      setBookingMenuOpen(true);
    } else {
      setBookingMenuOpen(false);
    }
  }, [inBookingMgmt]);

  useEffect(() => {
    if (inPackageMgmt) {
      setPackageMenuOpen(true);
    } else {
      setPackageMenuOpen(false);
    }
  }, [inPackageMgmt]);

  useEffect(() => {
    // Ensure modules are saved and retrieved persistently
    if (modules && modules.length > 0) {
      setLoadedModules(modules);
      localStorage.setItem("modules", JSON.stringify(modules));
    } else {
      const savedModules = localStorage.getItem("modules");
      if (savedModules) {
        setLoadedModules(JSON.parse(savedModules));
      }
    }
  }, [modules]);

  // Navigation links with corresponding module names
  const navigationLinks = [
    { path: "/user-management", name: "User Management" },
    { path: "/vendor-management", name: "Vendor Management" },
    { path: "/subadmin-management", name: "Sub admin Management" },
    { path: "/cms-management", name: "CMS Management" },
  ];

  // Check if the module_name exists in the loadedModules array
  const isModuleAccessible = (moduleName) =>
    loadedModules.some(
      (module) => module.module_id?.module_name === moduleName
    );

  // Get the privilege types for a specific module
  const getPrivileges = (moduleName) => {
    const privileges = loadedModules
      .filter((mod) => mod.module_id?.module_name === moduleName)
      .map((mod) => mod.privilege_type?.privilege_type);
    return privileges;
  };

  return (
    <>
      <div className="left-sidebar">
        <Navbar
          bg="custom-green"
          variant="dark"
          key="md"
          expand="md"
          className="sideNavBar bg-[#73885f]"
        >
          <div className="mobile-menu">
            <Navbar.Brand to="/user-management" className="w-40 m-0 p-3">
              <img
                src={Logo}
                alt="logo"
                className=" transform hover:scale-105 color-white transition-all duration-300 "
              />
            </Navbar.Brand>
            <Navbar.Toggle aria-controls="md" />
          </div>
          <Navbar.Offcanvas
            id="md"
            aria-labelledby="md"
            width="w-100"
            placement="end"
          >
            <Offcanvas.Header closeButton className="flex-shrink-0" />
            <Offcanvas.Body className="d-flex flex-column p-0">
              <div
                className="d-flex flex-column justify-content-between w-100 flex-1"
                style={{ minHeight: 0 }}
              >
                <Nav className="me-auto w-100 flex-1 sidebar-nav">
                  {/* Always visible Dashboard link */}
                  <NavLink
                    to="/dashboard"
                    className={({ isActive }) =>
                      isActive
                        ? "nav-link active dashboard-nav-link"
                        : "nav-link dashboard-nav-link"
                    }
                  >
                    <img
                      src={DashboardIcon}
                      alt="Dashboard"
                      className="nav-icon mr-2"
                    />
                    <span className="nav-text">Dashboard</span>
                  </NavLink>
                  <NavLink
                    to="/document-management"
                    className={({ isActive }) =>
                      isActive
                        ? "nav-link active dashboard-nav-link"
                        : "nav-link dashboard-nav-link"
                    }
                  >
                    <img
                      src={DocumentManagementIcon}
                      alt="Document Management"
                      className="nav-icon mr-2"
                    />
                    <span className="nav-text">Document Management</span>
                  </NavLink>

                  {canService ? (
                  <div className="w-100">
                    <div className="sidebar-product-mgmt-row">
                      <button
                        type="button"
                        className={`sidebar-pm-label-btn nav-link dashboard-nav-link d-flex align-items-center text-start border-0 bg-transparent text-white ${
                          inServiceMgmt ? "active" : ""
                        }`}
                        onClick={() => {
                          setServiceMenuOpen(true);
                          navigate("/service-management");
                        }}
                      >
                        <img
                          src={ServiceManagementIcon}
                          alt=""
                          className="nav-icon mr-2 flex-shrink-0"
                        />
                        <span className="nav-text">Service Management</span>
                      </button>
                    </div>
                    {serviceMenuOpen ? (
                      <div className="sidebar-product-submenu ps-2 ms-3 mt-1 mb-2">
                        <NavLink
                          to="/service-management"
                          end={false}
                          className={() => {
                            const path = routerLocation.pathname;
                            const on =
                              path.startsWith("/service-management") &&
                              !path.includes("/service-management/rejected-services") &&
                              !path.includes("/service-management/media-management");
                            return on
                              ? "nav-link active dashboard-nav-link sidebar-submenu-link text-white"
                              : "nav-link dashboard-nav-link sidebar-submenu-link text-white text-opacity-90";
                          }}
                        >
                          <span className="nav-text">Services</span>
                          <FaChevronRight
                            aria-hidden
                            className="sidebar-submenu-chevron"
                          />
                        </NavLink>
                        <NavLink
                          to="/service-management/media-management"
                          end={false}
                          className={({ isActive }) => {
                            const on =
                              isActive ||
                              routerLocation.pathname.includes(
                                "/service-management/media-management"
                              );
                            return on
                              ? "nav-link active dashboard-nav-link sidebar-submenu-link text-white"
                              : "nav-link dashboard-nav-link sidebar-submenu-link text-white text-opacity-90";
                          }}
                        >
                          <span className="nav-text">Media Management</span>
                          <FaChevronRight
                            aria-hidden
                            className="sidebar-submenu-chevron"
                          />
                        </NavLink>
                        <NavLink
                          to="/service-management/rejected-services"
                          end={false}
                          className={({ isActive }) => {
                            const on =
                              isActive ||
                              routerLocation.pathname.includes(
                                "/service-management/rejected-services"
                              );
                            return on
                              ? "nav-link active dashboard-nav-link sidebar-submenu-link text-white"
                              : "nav-link dashboard-nav-link sidebar-submenu-link text-white text-opacity-90";
                          }}
                        >
                          <span className="nav-text">Rejected Services</span>
                          <FaChevronRight
                            aria-hidden
                            className="sidebar-submenu-chevron"
                          />
                        </NavLink>
                      </div>
                    ) : null}
                  </div>
                  ) : null}
                  {canService ? (
                  <div className="w-100">
                    <div className="sidebar-product-mgmt-row">
                      <button
                        type="button"
                        className={`sidebar-pm-label-btn nav-link dashboard-nav-link d-flex align-items-center text-start border-0 bg-transparent text-white ${
                          inBookingMgmt ? "active" : ""
                        }`}
                        onClick={() => {
                          setBookingMenuOpen(true);
                          navigate("/booking-management/other-services", {
                            state: {
                              ...getBookingManagementNavLinkState(),
                              bookingSegment: "other-services",
                            },
                          });
                        }}
                      >
                        <img
                          src={BookingManagementIcon}
                          alt=""
                          className="nav-icon mr-2 flex-shrink-0"
                        />
                        <span className="nav-text">Booking Management</span>
                      </button>
                    </div>
                    {bookingMenuOpen ? (
                      <div className="sidebar-product-submenu ps-2 ms-3 mt-1 mb-2">
                        <NavLink
                          to="/booking-management/other-services"
                          end={false}
                          state={{
                            ...getBookingManagementNavLinkState(),
                            bookingSegment: "other-services",
                          }}
                          className={({ isActive }) => {
                            const on =
                              isActive ||
                              routerLocation.pathname === "/booking-management" ||
                              routerLocation.pathname.includes(
                                "/booking-management/other-services"
                              );
                            return on
                              ? "nav-link active dashboard-nav-link sidebar-submenu-link text-white"
                              : "nav-link dashboard-nav-link sidebar-submenu-link text-white text-opacity-90";
                          }}
                        >
                          <span className="nav-text">Other Services</span>
                          <FaChevronRight
                            aria-hidden
                            className="sidebar-submenu-chevron"
                          />
                        </NavLink>
                        <NavLink
                          to="/booking-management/catering-booking"
                          end={false}
                          state={{
                            ...getBookingManagementNavLinkState(),
                            bookingSegment: "catering-booking",
                          }}
                          className={({ isActive }) => {
                            const on =
                              isActive ||
                              routerLocation.pathname.includes(
                                "/booking-management/catering-booking"
                              );
                            return on
                              ? "nav-link active dashboard-nav-link sidebar-submenu-link text-white"
                              : "nav-link dashboard-nav-link sidebar-submenu-link text-white text-opacity-90";
                          }}
                        >
                          <span className="nav-text">Catering Booking</span>
                          <FaChevronRight
                            aria-hidden
                            className="sidebar-submenu-chevron"
                          />
                        </NavLink>
                      </div>
                    ) : null}
                  </div>
                  ) : null}
                  {canService ? (
                  <div className="w-100">
                    <div className="sidebar-product-mgmt-row">
                      <button
                        type="button"
                        className={`sidebar-pm-label-btn nav-link dashboard-nav-link d-flex align-items-center text-start border-0 bg-transparent text-white ${
                          inPackageMgmt ? "active" : ""
                        }`}
                        onClick={() => {
                          setPackageMenuOpen(true);
                          navigate("/package-management/inquiries");
                        }}
                      >
                        <img
                          src={PackageManagementIcon}
                          alt=""
                          className="nav-icon mr-2 flex-shrink-0"
                        />
                        <span className="nav-text">Package Management</span>
                      </button>
                    </div>
                    {packageMenuOpen ? (
                      <div className="sidebar-product-submenu ps-2 ms-3 mt-1 mb-2">
                        <NavLink
                          to="/package-management/inquiries"
                          end={false}
                          className={() => {
                            const path = routerLocation.pathname;
                            const on =
                              path === "/package-management" ||
                              path.startsWith("/package-management/inquiries");
                            return on
                              ? "nav-link active dashboard-nav-link sidebar-submenu-link text-white"
                              : "nav-link dashboard-nav-link sidebar-submenu-link text-white text-opacity-90";
                          }}
                        >
                          <span className="nav-text">Package Inquiries</span>
                          <FaChevronRight
                            aria-hidden
                            className="sidebar-submenu-chevron"
                          />
                        </NavLink>
                        <NavLink
                          to="/package-management/bookings"
                          end={false}
                          className={() => {
                            const on = routerLocation.pathname.startsWith(
                              "/package-management/bookings"
                            );
                            return on
                              ? "nav-link active dashboard-nav-link sidebar-submenu-link text-white"
                              : "nav-link dashboard-nav-link sidebar-submenu-link text-white text-opacity-90";
                          }}
                        >
                          <span className="nav-text">Bookings</span>
                          <FaChevronRight
                            aria-hidden
                            className="sidebar-submenu-chevron"
                          />
                        </NavLink>
                      </div>
                    ) : null}
                  </div>
                  ) : null}
                  <NavLink
                    to="/support-and-help"
                    className={() =>
                      routerLocation.pathname.startsWith("/support-and-help")
                        ? "nav-link active dashboard-nav-link"
                        : "nav-link dashboard-nav-link"
                    }
                  >
                    <FaHeadset
                      className="nav-icon mr-2 flex-shrink-0"
                      aria-hidden
                      style={{ width: "1.1rem", height: "1.1rem" }}
                    />
                    <span className="nav-text">Support & help</span>
                  </NavLink>
                  {canService ? (
                    <NavLink
                      to="/disputes"
                      className={() =>
                        routerLocation.pathname.startsWith("/disputes")
                          ? "nav-link active dashboard-nav-link"
                          : "nav-link dashboard-nav-link"
                      }
                    >
                      <img
                        src={BookingManagementIcon}
                        alt=""
                        className="nav-icon mr-2 flex-shrink-0"
                      />
                      <span className="nav-text">My disputes</span>
                    </NavLink>
                  ) : null}
                  {canShop || canRent ? (
                  <div className="w-100">
                    <div className="sidebar-product-mgmt-row">
                      <button
                        type="button"
                        className={`sidebar-pm-label-btn nav-link dashboard-nav-link d-flex align-items-center text-start border-0 bg-transparent text-white ${
                          inProductMgmt ? "active" : ""
                        }`}
                        onClick={() => {
                          setProductMenuOpen(true);
                          navigate(productHome);
                        }}
                      >
                        <img
                          src={ServiceManagementIcon}
                          alt=""
                          className="nav-icon mr-2 flex-shrink-0"
                        />
                        <span className="nav-text">Product Management</span>
                      </button>
                    </div>
                    {productMenuOpen ? (
                      <div className="sidebar-product-submenu ps-2 ms-3 mt-1 mb-2">
                        {canShop ? (
                        <NavLink
                          to="/product-management/shop"
                          end={false}
                          className={({ isActive }) => {
                            const on =
                              isActive ||
                              routerLocation.pathname.includes(
                                "/product-management/shop"
                              );
                            return on
                              ? "nav-link active dashboard-nav-link sidebar-submenu-link text-white"
                              : "nav-link dashboard-nav-link sidebar-submenu-link text-white text-opacity-90";
                          }}
                        >
                          <span className="nav-text">Shop</span>
                          <FaChevronRight
                            aria-hidden
                            className="sidebar-submenu-chevron"
                          />
                        </NavLink>
                        ) : null}
                        {canRent ? (
                        <NavLink
                          to="/product-management/rent"
                          end={false}
                          className={({ isActive }) => {
                            const on =
                              isActive ||
                              routerLocation.pathname.includes(
                                "/product-management/rent"
                              );
                            return on
                              ? "nav-link active dashboard-nav-link sidebar-submenu-link text-white"
                              : "nav-link dashboard-nav-link sidebar-submenu-link text-white text-opacity-90";
                          }}
                        >
                          <span className="nav-text">Rent</span>
                          <FaChevronRight
                            aria-hidden
                            className="sidebar-submenu-chevron"
                          />
                        </NavLink>
                        ) : null}
                        <NavLink
                          to="/product-management/offers"
                          end={false}
                          className={({ isActive }) => {
                            const on =
                              isActive ||
                              routerLocation.pathname.includes(
                                "/product-management/offers"
                              );
                            return on
                              ? "nav-link active dashboard-nav-link sidebar-submenu-link text-white"
                              : "nav-link dashboard-nav-link sidebar-submenu-link text-white text-opacity-90";
                          }}
                        >
                          <span className="nav-text">Offers</span>
                          <FaChevronRight
                            aria-hidden
                            className="sidebar-submenu-chevron"
                          />
                        </NavLink>
                      </div>
                    ) : null}
                  </div>
                  ) : null}

                  {canShop || canRent ? (
                  <NavLink
                    to="/inventory"
                    className={({ isActive }) =>
                      isActive
                        ? "nav-link active dashboard-nav-link"
                        : "nav-link dashboard-nav-link"
                    }
                  >
                    <img
                      src={DocumentManagementIcon}
                      alt=""
                      className="nav-icon mr-2 flex-shrink-0"
                    />
                    <span className="nav-text">Inventory</span>
                  </NavLink>
                  ) : null}

                  {canShop || canRent ? (
                  <div className="w-100">
                    <div className="sidebar-product-mgmt-row">
                      <button
                        type="button"
                        className={`sidebar-pm-label-btn nav-link dashboard-nav-link d-flex align-items-center text-start border-0 bg-transparent text-white ${
                          inMerchantMgmt ? "active" : ""
                        }`}
                        onClick={() => {
                          setMerchantMenuOpen(true);
                          navigate(merchantHome);
                        }}
                      >
                        <img
                          src={ServiceManagementIcon}
                          alt=""
                          className="nav-icon mr-2 flex-shrink-0"
                        />
                        <span className="nav-text">Merchant management</span>
                      </button>
                    </div>
                    {merchantMenuOpen ? (
                      <div className="sidebar-product-submenu ps-2 ms-3 mt-1 mb-2">
                        {canShop ? (
                        <NavLink
                          to="/merchant-management/shop"
                          end={false}
                          className={({ isActive }) => {
                            const on =
                              isActive ||
                              routerLocation.pathname.includes(
                                "/merchant-management/shop"
                              );
                            return on
                              ? "nav-link active dashboard-nav-link sidebar-submenu-link text-white"
                              : "nav-link dashboard-nav-link sidebar-submenu-link text-white text-opacity-90";
                          }}
                        >
                          <span className="nav-text">Shop</span>
                          <FaChevronRight
                            aria-hidden
                            className="sidebar-submenu-chevron"
                          />
                        </NavLink>
                        ) : null}
                        {canRent ? (
                        <NavLink
                          to="/merchant-management/rent"
                          end={false}
                          className={({ isActive }) => {
                            const on =
                              isActive ||
                              routerLocation.pathname.includes(
                                "/merchant-management/rent"
                              );
                            return on
                              ? "nav-link active dashboard-nav-link sidebar-submenu-link text-white"
                              : "nav-link dashboard-nav-link sidebar-submenu-link text-white text-opacity-90";
                          }}
                        >
                          <span className="nav-text">Rent</span>
                          <FaChevronRight
                            aria-hidden
                            className="sidebar-submenu-chevron"
                          />
                        </NavLink>
                        ) : null}
                      </div>
                    ) : null}
                  </div>
                  ) : null}

                  <NavLink
                    to="/Terms-&-Condition"
                    className={({ isActive }) =>
                      isActive
                        ? "nav-link active dashboard-nav-link"
                        : "nav-link dashboard-nav-link"
                    }
                  >
                    <img
                      src={TermsAndConditionIcon}
                      alt="Terms & Conditions"
                      className="nav-icon mr-2"
                    />
                    <span className="nav-text">Terms & Conditions</span>
                  </NavLink>
                  <NavLink
                    to="/Privacy-Policy"
                    className={({ isActive }) =>
                      isActive
                        ? "nav-link active dashboard-nav-link"
                        : "nav-link dashboard-nav-link"
                    }
                  >
                    <img
                      src={require("../../assets/image/privacy.png")}
                      alt="Privacy Policy"
                      className="nav-icon mr-2"
                    />
                    <span className="nav-text">Privacy Policy</span>
                  </NavLink>
                  <NavLink
                    to="/FAQ"
                    className={({ isActive }) =>
                      isActive
                        ? "nav-link active dashboard-nav-link"
                        : "nav-link dashboard-nav-link"
                    }
                  >
                    <img
                      src={require("../../assets/image/faq.png")}
                      alt="FAQ"
                      className="nav-icon mr-2"
                    />
                    <span className="nav-text">FAQ</span>
                  </NavLink>

                  {/* {navigationLinks.map(({ path, name }) =>
                  isModuleAccessible(name) ? (
                    <NavLink
                      key={path}
                      to={path}
                      state={{ privileges: getPrivileges(name) }} // Passing privileges to the route
                      className={({ isActive }) =>
                        isActive ? "nav-link active" : "nav-link"
                      }
                    >
                      <span className="nav-text">{name}</span>
                    </NavLink>
                  ) : null
                )} */}
                </Nav>

                <div className="mt-auto pt-4 pb-4 flex flex-col flex-shrink-0">
                  <div className="border-t border-[#E1E8D5] mx-4 mb-4"></div>
                  <NavLink
                    to="/My-Profile"
                    className={({ isActive }) =>
                      `px-4 flex items-center gap-3 no-underline hover:opacity-90 transition-opacity ${isActive ? "opacity-95" : ""}`
                    }
                  >
                    <div className="w-10 h-10 rounded-full overflow-hidden bg-[#C7F442] flex items-center justify-center flex-shrink-0">
                      <img
                        src={vendorProfile?.profile_pic || vendorDefaultAvatar}
                        alt="User avatar"
                        className="w-9 h-9 rounded-full object-cover"
                      />
                    </div>
                    <div className="min-w-0">
                      <p className="text-sm text-white font-Montserratmedium mb-0 truncate">
                        {vendorProfile?.name || "Vendor"}
                      </p>
                      <p className="text-xs text-[#FFFFFFB3] mb-0">Vendor</p>
                    </div>
                  </NavLink>
                </div>
              </div>
            </Offcanvas.Body>
          </Navbar.Offcanvas>
        </Navbar>
      </div>
    </>
  );
};

export default Sidebar;
